import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
    type ClipboardEvent,
    type ForwardedRef,
    type KeyboardEvent,
    type MouseEvent
} from 'react';
import {
    isSendKeystroke,
    SendKeyIcon,
    sendKeyToggleLabel,
    useSendOnEnter,
    withSendKeyHint
} from '@odo/shared-ui';
import { useAutoGrowTextarea } from '../../../../../hooks/useAutoGrowTextarea';
import { newClientRequestId } from './clientRequestId';
import { ComposerAttachmentArea } from './ComposerAttachmentArea';
import { ComposerDropOverlay } from './ComposerDropOverlay';
import { ComposerIconButton } from './ComposerIconButton';
import { ComposerSendButton } from './ComposerSendButton';
import { ComposerTools } from './ComposerTools';
import { filesFromClipboard } from './incomingFiles';
import type { OutgoingMessage } from './pendingMessages';
import { canSendDraft } from './stagedAttachment';
import type { ComposerAttachment } from './useComposerAttachment';
import { useComposerInsert } from './useComposerInsert';
import { useFileDropZone } from './useFileDropZone';

// About eight lines; past that the text box scrolls instead of pushing the thread up.
const COMPOSER_MAX_HEIGHT_PX = 200;

// Its own key: patient messages default to Shift+Enter sends, unlike the Conversations chat.
const SEND_ON_ENTER_KEY = 'dmComposerSendOnEnter';

const BOX_CLASSES =
    'relative flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-[border-color,box-shadow] focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100';

const TEXTAREA_CLASSES =
    'block w-full resize-none bg-transparent px-3 pt-2.5 pb-1 text-sm leading-5 text-gray-900 placeholder:text-gray-400 outline-none';

// For thread items that fill the composer, e.g. the AI draft; only the text changes.
export interface DmComposerHandle {
    readText: () => string;
    replaceText: (text: string) => void;
}

interface DmMessageComposerProps {
    patientId: string;
    attachment: ComposerAttachment;
    isSending: boolean;
    onSend: (outgoing: OutgoingMessage) => void;
}

function DmMessageComposerInner(
    { patientId, attachment, isSending, onSend }: DmMessageComposerProps,
    ref: ForwardedRef<DmComposerHandle>
) {
    const [text, setText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const insert = useComposerInsert(textareaRef, text, setText);
    useAutoGrowTextarea(textareaRef, text, COMPOSER_MAX_HEIGHT_PX);
    const { isDragOver, dropHandlers } = useFileDropZone(attachment.setFiles);
    const { sendOnEnter, toggleSendOnEnter } = useSendOnEnter(SEND_ON_ENTER_KEY, false);

    const canSend = canSendDraft(text, attachment.staged) && !isSending;

    useImperativeHandle(
        ref,
        () => ({
            readText: () => text,
            replaceText: (next: string) => {
                setText(next);
                // Focus once React has rendered the new value, with the caret at its end.
                requestAnimationFrame(() => {
                    const textarea = textareaRef.current;
                    textarea?.focus();
                    textarea?.setSelectionRange(next.length, next.length);
                });
            }
        }),
        [text]
    );

    const handleSend = useCallback(() => {
        if (!canSend) return;
        onSend({
            clientRequestId: newClientRequestId(),
            patientId,
            text: text.trim(),
            attachment: attachment.staged
        });
        setText('');
        attachment.clear();
        textareaRef.current?.focus();
    }, [canSend, onSend, patientId, text, attachment]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isSendKeystroke(e.nativeEvent, sendOnEnter)) return;
        e.preventDefault();
        handleSend();
    };

    const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
        const files = filesFromClipboard(e.clipboardData);
        if (files.length === 0) return;
        e.preventDefault();
        attachment.setFiles(files);
    };

    // Clicking the box's empty padding or bar behaves like clicking the text, as in chat apps.
    const focusTextFromEmptySpace = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === boxRef.current || e.target === barRef.current) {
            textareaRef.current?.focus();
        }
    };

    return (
        <div className="flex-shrink-0 pt-3">
            <div
                ref={boxRef}
                {...dropHandlers}
                onClick={focusTextFromEmptySpace}
                className={BOX_CLASSES}
            >
                {isDragOver && <ComposerDropOverlay />}
                <ComposerAttachmentArea attachment={attachment} />
                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={withSendKeyHint('Type a message…', sendOnEnter)}
                    aria-label="Message to patient"
                    rows={2}
                    className={TEXTAREA_CLASSES}
                />
                <div
                    ref={barRef}
                    className="flex items-center justify-between gap-2 px-1.5 pb-1.5 pt-0.5"
                >
                    <ComposerTools
                        patientId={patientId}
                        anchorRef={boxRef}
                        onInsert={insert}
                        onAttachFiles={attachment.setFiles}
                        onAttachFile={attachment.setFile}
                        attachNotice={attachment.notice}
                        onQuickReplyMedia={attachment.setQuickReplyMedia}
                    />
                    <div className="flex shrink-0 items-center gap-1">
                        <ComposerIconButton
                            label={sendKeyToggleLabel(sendOnEnter)}
                            pressed={sendOnEnter}
                            onClick={toggleSendOnEnter}
                        >
                            <SendKeyIcon className="w-4 h-4" />
                        </ComposerIconButton>
                        <ComposerSendButton
                            isSending={isSending}
                            disabled={!canSend}
                            onClick={handleSend}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

const DmMessageComposer = forwardRef(DmMessageComposerInner);

export { DmMessageComposer };
